import React, { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { IntegrationServiceId, TASK_EXPORT_SUPPORTED_INTEGRATIONS } from '../../../common/integrations'
import { writeCopyContentToClipboard } from '../../../apps/professional-app/fa/MeetingSummary/copy'

import { Divider } from '../../../components/Divider/Divider'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { Button } from '../../../components/Button/Button'

import { selectContactsForThread, selectEmailCurrentUser } from '../../../store/emailHub/emailUsers'
import { selectGroupedTasks, selectTaskPollingEnabled, selectTasksLoading } from '../../../store/task/task'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { addToast } from '../../../store/toast/toastSlice'

import { AddUserGroupTasks, NewFormTask, UserGroupTasks } from '../../Tasks/Tasks'
import { ExternalUserServiceIntegration } from '../../../models/User'
import { IntegrationExportButton } from '../../../components/IntegrationExportButton/IntegrationExportButton'
import { copyGroupedTasks } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/CustomDisplay/ClientAndAdvisorTasks/utils'

export interface EmailResponseTasksProps {
  emailConnector: string
  threadId: string
  emailId: string
}

export const EmailResponseTasks: React.FC<EmailResponseTasksProps> = ({ emailConnector, threadId, emailId }) => {
  return (
    <div className='p-4 zocks-border-0.5 border border-black d-flex flex-column gap-4'>
      <EmailResponseTasksContentHeader threadId={threadId} />
      <EmailResponseTasksContentBody emailConnector={emailConnector} threadId={threadId} emailId={emailId} />
    </div>
  )
}

interface EmailResponseTasksContentHeaderProps {
  threadId: string
}

const EmailResponseTasksContentHeader: React.FC<EmailResponseTasksContentHeaderProps> = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const currentUser = useAppSelector(selectEmailCurrentUser)
  const groupedTasks = useAppSelector(selectGroupedTasks)

  const taskExportSupportedUserIntegrations = useMemo(
    () => currentUser?.integrations?.filter(({ serviceId }) => TASK_EXPORT_SUPPORTED_INTEGRATIONS.includes(serviceId as IntegrationServiceId)) ?? [],
    [currentUser],
  )

  const handleTaskExportButtonClicked = (integration: ExternalUserServiceIntegration) => {
    navigate(`modal/export-tasks/connectors/${integration.serviceId}`)
  }

  const handleCopy = useCallback(async () => {
    const copyContent = copyGroupedTasks(groupedTasks)
    await writeCopyContentToClipboard(copyContent)
    dispatch(addToast({ variant: 'success', title: 'Copied', messages: ['Copied to Clipboard!'] }))
  }, [dispatch, groupedTasks])

  return (
    <div className='d-flex align-items-center justify-content-between'>
      <span className='paragraph-large bold'>Tasks</span>
      <div className='d-flex align-items-center gap-2'>
        <Tooltip className='paragraph-small' title='Copy all' placement='top' offset={[0, 10]}>
          <Button className='px-2' variant='header' onClick={handleCopy}>
            <i className='bi bi-copy copy-all-icon' />
          </Button>
        </Tooltip>
        {taskExportSupportedUserIntegrations.map((integration) => (
          <IntegrationExportButton key={integration.serviceId} serviceId={integration.serviceId} onClick={() => handleTaskExportButtonClicked(integration)} />
        ))}
      </div>
    </div>
  )
}

interface EmailResponseTasksContentBodyProps {
  emailConnector: string
  threadId: string
  emailId: string
}

const EmailResponseTasksContentBody: React.FC<EmailResponseTasksContentBodyProps> = ({ emailConnector, threadId, emailId }) => {
  const isLoading = useAppSelector(selectTasksLoading)
  const isPolling = useAppSelector(selectTaskPollingEnabled)
  const groupedTasks = useAppSelector(selectGroupedTasks)

  const contactsForThread = useAppSelector(selectContactsForThread(threadId))

  const formTaskToCreateTaskPayload = useCallback(
    (assignedToUserId: string, formTask: NewFormTask) => {
      const { subject, description, relatedToUserId } = formTask
      return {
        ...(relatedToUserId ? { relatedTo: { type: 'user', id: relatedToUserId } } : {}),
        ...(description ? { description } : {}),
        assignedTo: { type: 'user', id: assignedToUserId },
        task: subject,
        type: 'email',
        emailConnector,
        threadId,
        emailId,
      }
    },
    [emailConnector, emailId, threadId],
  )

  if (isLoading || isPolling) {
    return (
      <div className='skeleton mt-2' style={{ width: '100%', height: '150px' }}>
        <div className='d-flex flex-column align-items-center justify-content-center h-100'>
          <div>Loading…</div>
        </div>
      </div>
    )
  }

  const initialRelatedToUser = contactsForThread.length === 1 ? contactsForThread[0] : null

  return (
    <>
      {groupedTasks.map(({ user, tasks }, index) => (
        <React.Fragment key={user.id}>
          <UserGroupTasks
            user={user}
            tasks={tasks}
            row={index}
            formTaskToCreateTaskPayload={formTaskToCreateTaskPayload}
            initialRelatedToUser={initialRelatedToUser}
          />
          {index !== groupedTasks.length - 1 ? <Divider /> : null}
        </React.Fragment>
      ))}
      <AddUserGroupTasks formTaskToCreateTaskPayload={formTaskToCreateTaskPayload} initialRelatedToUser={initialRelatedToUser} />
    </>
  )
}
